import React, { useCallback, useEffect, useState } from 'react';
import Modal from './Modal';
import client from '../api/client';
import {
  KycVerification,
  P2pDispute,
  PaginatedResponse,
  UserActivity,
  UserFullProfile,
  UserVerificationField,
} from '../types';
import { useUserNames, formatUserLabel } from '../hooks/useUserNames';
import { useAuth } from '../../features/auth/AuthContext';
import AuthImage from '../../features/kyc/AuthImage';
import { getUserFullProfile, updateUserVerifications } from '../../features/users/api';

interface UserDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number | null;
  title?: string;
}

interface ActivityListResponse {
  success?: boolean;
  data: UserActivity[];
  meta?: { current_page: number; last_page: number; per_page: number; total: number };
}

interface P2pBackendResponse<T> {
  status: string;
  data: T;
}

type Tab = 'profile' | 'verifications' | 'kyc' | 'sessions' | 'trades';

const fmt = (d: string | null | undefined): string =>
  d ? new Date(d).toLocaleString() : '—';

const LEVEL_LABELS: Record<number, string> = {
  0: 'Unregistered',
  1: 'Instant',
  2: 'Email verified',
  3: 'Phone verified',
  4: 'Basic KYC',
  5: 'Advanced KYC',
};

const extractError = (err: unknown, fallback: string): string =>
  err && typeof err === 'object' && 'response' in err
    ? ((err as { response?: { data?: { error?: string; message?: string } } }).response?.data
        ?.error ??
      (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
      fallback)
    : fallback;

const TabButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
      active
        ? 'bg-indigo-500/15 text-indigo-300'
        : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
    }`}
  >
    {children}
  </button>
);

const VerifiedBadge: React.FC<{ ok: boolean; okLabel?: string; noLabel?: string }> = ({
  ok,
  okLabel = 'Verified',
  noLabel = 'Not verified',
}) => (
  <span className={`badge ${ok ? 'badge-success' : 'badge-danger'}`}>
    {ok ? okLabel : noLabel}
  </span>
);

const ProfileTab: React.FC<{ profile: UserFullProfile; firstActivity: UserActivity | null }> = ({
  profile,
  firstActivity,
}) => {
  const { user, verifications, security } = profile;
  const socials = [
    security.google_linked && 'Google',
    security.apple_linked && 'Apple',
    security.telegram_linked && 'Telegram',
  ].filter(Boolean);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500/30 to-purple-600/30 border border-indigo-500/20 flex items-center justify-center">
          <span className="text-lg font-semibold text-indigo-300">
            {user.name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div>
          <p className="text-base font-semibold text-white">{user.full_name || user.name}</p>
          <p className="text-xs text-slate-500">{user.email}</p>
          {user.username && <p className="text-xs text-slate-500">@{user.username}</p>}
        </div>
        <div className="ml-auto text-right">
          <span className="badge badge-info">
            Level {verifications.level} — {LEVEL_LABELS[verifications.level] ?? verifications.level_name}
          </span>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
        <dt className="text-slate-500">User ID</dt>
        <dd className="text-slate-200 font-mono">#{user.id}</dd>

        <dt className="text-slate-500">Role</dt>
        <dd className="text-slate-200 capitalize">{user.role}</dd>

        <dt className="text-slate-500">Status</dt>
        <dd>
          <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
            {user.is_active ? 'Active' : 'Inactive'}
          </span>
        </dd>

        <dt className="text-slate-500">Phone</dt>
        <dd className="text-slate-200">{user.phone_number ?? '—'}</dd>

        <dt className="text-slate-500">Address</dt>
        <dd className="text-slate-200">{user.address ?? '—'}</dd>

        <dt className="text-slate-500">Password set</dt>
        <dd className="text-slate-200">{security.has_password ? 'Yes' : 'No (social/OTP login)'}</dd>

        <dt className="text-slate-500">2FA (TOTP)</dt>
        <dd>
          <VerifiedBadge ok={security.totp_enabled} okLabel="Enabled" noLabel="Disabled" />
        </dd>

        <dt className="text-slate-500">Whitelist-only withdrawals</dt>
        <dd className="text-slate-200">{security.whitelist_only_withdrawals ? 'Yes' : 'No'}</dd>

        <dt className="text-slate-500">Linked socials</dt>
        <dd className="text-slate-200">{socials.length > 0 ? socials.join(', ') : '—'}</dd>

        <dt className="text-slate-500">Registered</dt>
        <dd className="text-slate-200">{fmt(user.created_at)}</dd>

        <dt className="text-slate-500">Registration IP</dt>
        <dd className="text-slate-200 font-mono">{firstActivity?.ip_address ?? '—'}</dd>
      </dl>
    </div>
  );
};

interface VerificationRowDef {
  field: UserVerificationField;
  label: string;
  detail: string | null;
  verified: boolean;
  verifiedAt: string | null;
  canVerify: boolean;
  disabledReason?: string;
}

const VerificationsTab: React.FC<{
  profile: UserFullProfile;
  canEdit: boolean;
  busyField: UserVerificationField | null;
  onToggle: (field: UserVerificationField, value: boolean, label: string) => void;
}> = ({ profile, canEdit, busyField, onToggle }) => {
  const { verifications } = profile;

  const rows: VerificationRowDef[] = [
    {
      field: 'email_verified',
      label: 'Email',
      detail: verifications.email.value ?? null,
      verified: verifications.email.verified,
      verifiedAt: verifications.email.verified_at,
      canVerify: true,
    },
    {
      field: 'phone_verified',
      label: 'Phone',
      detail: verifications.phone.value ?? null,
      verified: verifications.phone.verified,
      verifiedAt: verifications.phone.verified_at,
      canVerify: !!verifications.phone.value,
      disabledReason: verifications.phone.value ? undefined : 'User has no phone number',
    },
    {
      field: 'kyc_basic_verified',
      label: 'KYC — Basic',
      detail: null,
      verified: verifications.kyc_basic.verified,
      verifiedAt: verifications.kyc_basic.verified_at,
      canVerify: true,
    },
    {
      field: 'kyc_advanced_verified',
      label: 'KYC — Advanced',
      detail: null,
      verified: verifications.kyc_advanced.verified,
      verifiedAt: verifications.kyc_advanced.verified_at,
      canVerify: true,
    },
    {
      field: 'totp_enabled',
      label: '2FA (TOTP)',
      detail: null,
      verified: verifications.totp.enabled,
      verifiedAt: null,
      canVerify: false,
      disabledReason: 'Only the user can enable 2FA (requires their authenticator secret)',
    },
  ];

  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <div
          key={row.field}
          className="card flex items-center gap-3 px-4 py-3"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-white">{row.label}</p>
              <VerifiedBadge
                ok={row.verified}
                okLabel={row.field === 'totp_enabled' ? 'Enabled' : 'Verified'}
                noLabel={row.field === 'totp_enabled' ? 'Disabled' : 'Not verified'}
              />
            </div>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              {row.detail ?? ''}
              {row.detail && row.verifiedAt ? ' · ' : ''}
              {row.verifiedAt ? `verified ${fmt(row.verifiedAt)}` : ''}
              {!row.detail && !row.verifiedAt ? '—' : ''}
            </p>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              {!row.verified && (
                <button
                  type="button"
                  disabled={!row.canVerify || busyField !== null}
                  title={row.disabledReason}
                  onClick={() => onToggle(row.field, true, row.label)}
                  className="px-3 py-1.5 text-xs font-medium text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busyField === row.field ? '…' : 'Verify'}
                </button>
              )}
              {row.verified && (
                <button
                  type="button"
                  disabled={busyField !== null}
                  onClick={() => onToggle(row.field, false, row.label)}
                  className="px-3 py-1.5 text-xs font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg transition-all disabled:opacity-40"
                >
                  {busyField === row.field ? '…' : row.field === 'totp_enabled' ? 'Disable' : 'Revoke'}
                </button>
              )}
            </div>
          )}
        </div>
      ))}
      {!canEdit && (
        <p className="text-xs text-slate-500">
          You need the &quot;Edit Users&quot; permission to change verification states.
        </p>
      )}
    </div>
  );
};

const kycStatusBadge = (status: KycVerification['status']): string => {
  switch (status) {
    case 'approved':
      return 'badge-success';
    case 'rejected':
      return 'badge-danger';
    case 'under_review':
      return 'badge-info';
    default:
      return 'badge-warning';
  }
};

const KycTab: React.FC<{ submissions: KycVerification[] }> = ({ submissions }) => {
  const [openId, setOpenId] = useState<number | null>(submissions[0]?.id ?? null);

  if (submissions.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-6">No KYC submissions.</p>;
  }

  return (
    <div className="space-y-2">
      {submissions.map((s) => {
        const open = openId === s.id;
        return (
          <div key={s.id} className="rounded-lg border border-slate-800 overflow-hidden">
            <button
              type="button"
              onClick={() => setOpenId(open ? null : s.id)}
              className="w-full flex items-center gap-3 px-4 py-3 bg-slate-800/30 hover:bg-slate-800/50 transition-colors text-left"
            >
              <span className="font-mono text-indigo-400 text-sm">#{s.id}</span>
              <span className="badge badge-info capitalize">{s.level}</span>
              <span className={`badge ${kycStatusBadge(s.status)}`}>{s.status.replace('_', ' ')}</span>
              <span className="text-xs text-slate-500 ml-auto">
                {s.submitted_at ? `submitted ${fmt(s.submitted_at)}` : `created ${fmt(s.created_at)}`}
              </span>
              <span className="text-slate-500 text-xs">{open ? '▲' : '▼'}</span>
            </button>
            {open && (
              <div className="p-4 space-y-4">
                <dl className="grid grid-cols-2 gap-y-1.5 gap-x-4 text-sm">
                  <dt className="text-slate-500">Full name</dt>
                  <dd className="text-slate-200">
                    {[s.last_name, s.first_name, s.middle_name].filter(Boolean).join(' ') || '—'}
                  </dd>
                  <dt className="text-slate-500">Date of birth</dt>
                  <dd className="text-slate-200">{s.date_of_birth ?? '—'}</dd>
                  <dt className="text-slate-500">Nationality</dt>
                  <dd className="text-slate-200">{s.nationality ?? '—'}</dd>
                  <dt className="text-slate-500">Residence</dt>
                  <dd className="text-slate-200">
                    {[s.country_of_residence, s.city, s.address, s.postal_code]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </dd>
                  <dt className="text-slate-500">Document</dt>
                  <dd className="text-slate-200">
                    {s.document_type ? `${s.document_type.replace('_', ' ')} · ` : ''}
                    {s.document_number ?? '—'}
                    {s.document_country ? ` (${s.document_country})` : ''}
                  </dd>
                  <dt className="text-slate-500">Document expiry</dt>
                  <dd className="text-slate-200">{s.document_expiry_date ?? '—'}</dd>
                  <dt className="text-slate-500">Reviewed</dt>
                  <dd className="text-slate-200">
                    {s.reviewed_at
                      ? `${fmt(s.reviewed_at)}${s.reviewed_by_admin_id ? ` by admin #${s.reviewed_by_admin_id}` : ''}`
                      : '—'}
                  </dd>
                  {s.rejection_reason && (
                    <>
                      <dt className="text-slate-500">Rejection reason</dt>
                      <dd className="text-red-400">{s.rejection_reason}</dd>
                    </>
                  )}
                  {s.internal_notes && (
                    <>
                      <dt className="text-slate-500">Internal notes</dt>
                      <dd className="text-amber-300/90">{s.internal_notes}</dd>
                    </>
                  )}
                </dl>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {(
                    [
                      ['Front', s.document_front_path],
                      ['Back', s.document_back_path],
                      ['Selfie', s.selfie_path],
                      ['Proof of address', s.proof_of_address_path],
                    ] as Array<[string, string | null | undefined]>
                  ).map(([label, path]) => (
                    <div key={label} className="space-y-1">
                      <p className="text-xs text-slate-500">{label}</p>
                      <AuthImage
                        path={path}
                        alt={label}
                        className="w-full h-28 object-cover rounded-lg border border-slate-800"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const SessionsTab: React.FC<{ sessions: UserActivity[]; loading: boolean }> = ({
  sessions,
  loading,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-8 h-8 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (sessions.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-6">No login sessions recorded.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-800/40">
          <tr>
            <th className="table-header">When</th>
            <th className="table-header">IP</th>
            <th className="table-header">Device</th>
            <th className="table-header">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {sessions.map((s) => (
            <tr key={s.id}>
              <td className="px-3 py-2 text-slate-300 whitespace-nowrap">
                {fmt(s.created_at)}
              </td>
              <td className="px-3 py-2 text-slate-300 font-mono">
                {s.ip_address ?? '—'}
              </td>
              <td className="px-3 py-2 text-slate-400 max-w-[280px] truncate" title={s.user_agent ?? ''}>
                {s.user_agent ?? '—'}
              </td>
              <td className="px-3 py-2">
                <span className={`badge ${s.status === 'success' ? 'badge-success' : 'badge-warning'}`}>
                  {s.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TradesTab: React.FC<{ trades: P2pDispute[]; loading: boolean; userId: number | null }> = ({
  trades,
  loading,
  userId,
}) => {
  const counterpartyIds = trades.flatMap((t) => [t.buyer_id, t.seller_id]);
  const userNames = useUserNames(counterpartyIds);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-8 h-8 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (trades.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-6">No trades found for this user.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-800/40">
          <tr>
            <th className="table-header">Trade</th>
            <th className="table-header">Role</th>
            <th className="table-header">Counterparty</th>
            <th className="table-header">Amount</th>
            <th className="table-header">Status</th>
            <th className="table-header">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {trades.map((t) => {
            const isBuyer = userId !== null && t.buyer_id === userId;
            const role = isBuyer ? 'Buyer' : t.seller_id === userId ? 'Seller' : '—';
            const counterparty = isBuyer ? t.seller_id : t.buyer_id;
            return (
              <tr key={t.id}>
                <td className="px-3 py-2 font-mono text-indigo-400">#{t.id}</td>
                <td className="px-3 py-2">
                  <span
                    className={`badge ${
                      role === 'Buyer'
                        ? 'badge-success'
                        : role === 'Seller'
                          ? 'badge-warning'
                          : 'badge-info'
                    }`}
                  >
                    {role}
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-300" title={`#${counterparty}`}>
                  {formatUserLabel(counterparty, userNames)}
                </td>
                <td className="px-3 py-2 text-slate-300">
                  {t.amount} {t.currency}
                </td>
                <td className="px-3 py-2">
                  <span className="badge badge-info">{t.status}</span>
                </td>
                <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{fmt(t.created_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const UserDetailModal: React.FC<UserDetailModalProps> = ({ isOpen, onClose, userId, title }) => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('users.edit');

  const [tab, setTab] = useState<Tab>('profile');
  const [profile, setProfile] = useState<UserFullProfile | null>(null);
  const [sessions, setSessions] = useState<UserActivity[]>([]);
  const [trades, setTrades] = useState<P2pDispute[]>([]);
  const [firstActivity, setFirstActivity] = useState<UserActivity | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyField, setBusyField] = useState<UserVerificationField | null>(null);

  const reset = useCallback(() => {
    setTab('profile');
    setProfile(null);
    setSessions([]);
    setTrades([]);
    setFirstActivity(null);
    setError(null);
    setActionError(null);
    setBusyField(null);
  }, []);

  const loadAll = useCallback(async (id: number) => {
    setLoadingProfile(true);
    setLoadingSessions(true);
    setLoadingTrades(true);
    setError(null);

    try {
      const [p, sess, tr] = await Promise.all([
        getUserFullProfile(id),
        client
          .get<ActivityListResponse>(`/user-activities/${id}`, {
            params: { type: 'user.login', per_page: 25 },
          })
          .then((r) => r.data.data ?? [])
          .catch(() => []),
        client
          .get<P2pBackendResponse<PaginatedResponse<P2pDispute>>>('/p2p/trades', {
            params: { user_id: String(id), per_page: 25 },
          })
          .then((r) => r.data.data?.data ?? [])
          .catch(() => []),
      ]);

      setProfile(p);
      setSessions(sess);
      setTrades(tr);

      const first = await client
        .get<ActivityListResponse>(`/user-activities/${id}`, {
          params: { type: 'user.registered', per_page: 1 },
        })
        .then((r) => r.data.data?.[0] ?? null)
        .catch(() => null);
      setFirstActivity(first);
    } catch (err) {
      setError(extractError(err, 'Failed to load user'));
    } finally {
      setLoadingProfile(false);
      setLoadingSessions(false);
      setLoadingTrades(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      reset();
      return;
    }
    if (userId) {
      loadAll(userId);
    }
  }, [isOpen, userId, loadAll, reset]);

  const handleToggleVerification = useCallback(
    async (field: UserVerificationField, value: boolean, label: string) => {
      if (!userId) return;

      const verb = field === 'totp_enabled' ? 'disable' : value ? 'verify' : 'revoke';
      if (!window.confirm(`Are you sure you want to ${verb} "${label}" for this user?`)) {
        return;
      }

      setActionError(null);
      setBusyField(field);
      try {
        await updateUserVerifications(userId, { [field]: value });
        const p = await getUserFullProfile(userId);
        setProfile(p);
      } catch (err) {
        setActionError(extractError(err, 'Failed to update verification'));
      } finally {
        setBusyField(null);
      }
    },
    [userId],
  );

  const user = profile?.user ?? null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title ?? (user ? `${user.name} — #${user.id}` : `User #${userId ?? ''}`)}
      size="3xl"
    >
      <div className="space-y-4">
        <div className="flex items-center gap-1.5 border-b border-slate-800 pb-3 overflow-x-auto">
          <TabButton active={tab === 'profile'} onClick={() => setTab('profile')}>
            Profile
          </TabButton>
          <TabButton active={tab === 'verifications'} onClick={() => setTab('verifications')}>
            Verifications
          </TabButton>
          <TabButton active={tab === 'kyc'} onClick={() => setTab('kyc')}>
            KYC ({profile?.kyc_submissions.length ?? 0})
          </TabButton>
          <TabButton active={tab === 'sessions'} onClick={() => setTab('sessions')}>
            Sessions ({sessions.length})
          </TabButton>
          <TabButton active={tab === 'trades'} onClick={() => setTab('trades')}>
            Trades ({trades.length})
          </TabButton>
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
            {error}
          </div>
        )}
        {actionError && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
            {actionError}
          </div>
        )}

        {loadingProfile ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-8 h-8 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {tab === 'profile' && profile && (
              <ProfileTab profile={profile} firstActivity={firstActivity} />
            )}
            {tab === 'verifications' && profile && (
              <VerificationsTab
                profile={profile}
                canEdit={canEdit}
                busyField={busyField}
                onToggle={handleToggleVerification}
              />
            )}
            {tab === 'kyc' && profile && <KycTab submissions={profile.kyc_submissions} />}
            {tab === 'sessions' && <SessionsTab sessions={sessions} loading={loadingSessions} />}
            {tab === 'trades' && <TradesTab trades={trades} loading={loadingTrades} userId={userId} />}
          </>
        )}
      </div>
    </Modal>
  );
};

export default UserDetailModal;
